import React, { useState, useEffect, useCallback } from 'react';
import { User, Shield, Lock, Check, Loader2, KeyRound } from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';
import ActiveSessions from '../../../components/admin/ActiveSessions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_DESCRIPTIONS = {
  super_admin: 'Full access to all modules, user management, settings, and activity log.',
  content_admin: 'Manage testimonials, updates, site images, programs, and public content.',
  finance_admin: 'Manage expenses, reimbursements, trips, and finance reports.',
  events_admin: 'Manage events, attendees, RSVPs, and event completion.',
  viewer: 'Read-only access across the admin portal.',
};

const AdminProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nameEdit, setNameEdit] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/profile/me`, { withCredentials: true });
      setProfile(res.data);
      setNewName(res.data.name || '');
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleNameSave = async () => {
    if (!newName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/profile/name`, { name: newName.trim() }, { withCredentials: true });
      setProfile(p => ({ ...p, name: newName.trim() }));
      setNameEdit(false);
    } catch (e) { alert('Failed to update name'); }
    setSavingName(false);
  };

  const handlePasswordChange = async () => {
    setPwError('');
    setPwSuccess(false);
    if (!pwForm.current_password || !pwForm.new_password) {
      setPwError('Both fields are required'); return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError('New password must be at least 8 characters'); return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError('New passwords do not match'); return;
    }
    setSavingPw(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/profile/password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      setPwSuccess(true);
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      setPwError(e.response?.data?.detail || 'Failed to change password');
    }
    setSavingPw(false);
  };

  if (loading || !profile) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;

  const roleLabel = profile.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-profile-tab">
      <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">My Profile</h2>

      {/* Profile Card */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="profile-card">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#BA1826] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {profile.name?.charAt(0) || '?'}
          </div>
          <div className="flex-1">
            {nameEdit ? (
              <div className="flex items-center gap-2">
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  className="px-3 py-1.5 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none flex-1"
                  data-testid="profile-name-input" />
                <button onClick={handleNameSave} disabled={savingName} className="btn-primary text-xs px-3 py-1.5" data-testid="profile-save-name">
                  {savingName ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </button>
                <button onClick={() => { setNameEdit(false); setNewName(profile.name); }} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">{profile.name}</h3>
                <button onClick={() => setNameEdit(true)} className="text-xs text-[#BA1826] hover:underline" data-testid="edit-name-btn">Edit</button>
              </div>
            )}
            <p className="text-sm text-[#4B5563]">{profile.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#F8F9FA] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-[#BA1826]" />
              <span className="text-xs font-semibold text-[#4B5563]">Role</span>
            </div>
            <p className="font-medium text-sm text-[#0A0A0A]" data-testid="profile-role">{roleLabel}</p>
            <p className="text-[10px] text-[#9CA3AF] mt-1">{ROLE_DESCRIPTIONS[profile.role] || ''}</p>
          </div>
          <div className="bg-[#F8F9FA] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4 text-[#BA1826]" />
              <span className="text-xs font-semibold text-[#4B5563]">Permissions</span>
            </div>
            <p className="font-medium text-sm text-[#0A0A0A]">{profile.permissions?.length || 0} total</p>
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              {(profile.permissions || []).filter(p => p.endsWith(':write')).length} write, {' '}
              {(profile.permissions || []).filter(p => p.endsWith(':read')).length} read
            </p>
          </div>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="password-change-card">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-[#BA1826]" />
          <h3 className="font-heading font-semibold text-sm">Change Password</h3>
        </div>

        {pwSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-700" data-testid="pw-success">
            Password changed successfully.
          </div>
        )}
        {pwError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700" data-testid="pw-error">
            {pwError}
          </div>
        )}

        <div className="space-y-3 max-w-sm">
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Current Password</label>
            <input type="password" value={pwForm.current_password}
              onChange={(e) => setPwForm(f => ({ ...f, current_password: e.target.value }))}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="current-password-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">New Password</label>
            <input type="password" value={pwForm.new_password}
              onChange={(e) => setPwForm(f => ({ ...f, new_password: e.target.value }))}
              placeholder="Min 8 characters"
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="new-password-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4B5563] mb-1">Confirm New Password</label>
            <input type="password" value={pwForm.confirm}
              onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="confirm-password-input" />
          </div>
          <button onClick={handlePasswordChange} disabled={savingPw}
            className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="change-password-btn">
            {savingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            Change Password
          </button>
        </div>
      </div>

      {/* Active Sessions */}
      <ActiveSessions scope="me" />
    </div>
  );
};

export default AdminProfileTab;
